import { useEffect, useRef } from "react";

/**
 * Sequence values are sample numbers, 1-based:
 *   0 / false = rest
 *   1 / true  = sampleUrls[0]
 *   2         = sampleUrls[1], etc.
 */
const RhythmPlayback = ({
  sequence,
  bpm,
  isPlaying,
  sampleUrls,
  setIsPlaying = null,
  onCurrentBeatChange,
  reset_playback = null,
  isPlaylist = false,
  nextelement = null,
  loop = true,
}) => {
  const audioCtxRef = useRef(null);
  const buffersRef = useRef([]);
  const currentBeatRef = useRef(0);
  const nextNoteTimeRef = useRef(0);
  const timerRef = useRef(null);
  const runningRef = useRef(false);
  const visualTimeoutsRef = useRef([]);

  const lookahead = 25; // ms between scheduler runs
  const scheduleAheadTime = 0.1; // seconds of audio scheduled ahead

  // Always read the LATEST props inside the scheduler loop. Without this,
  // the setTimeout loop keeps the props from the render where Start was
  // pressed (stale closure): BPM, loop and sequence changes are ignored,
  // and buffers that finish loading after Start are never seen.
  const propsRef = useRef({});
  propsRef.current = {
    sequence, bpm, loop, onCurrentBeatChange, reset_playback,
    setIsPlaying, isPlaylist, nextelement,
  };

  const getCtx = () => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  };

  // Load samples
  useEffect(() => {
    let cancelled = false;
    const ctx = getCtx();
    Promise.all(
      (sampleUrls || []).map(async (url) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status} loading ${url}`);
        return ctx.decodeAudioData(await res.arrayBuffer());
      })
    )
      .then((buffers) => { if (!cancelled) buffersRef.current = buffers; })
      .catch((err) => console.error("RhythmPlayback: error loading samples:", err));
    return () => { cancelled = true; };
  }, [sampleUrls]);

  const clearVisualTimeouts = () => {
    visualTimeoutsRef.current.forEach(clearTimeout);
    visualTimeoutsRef.current = [];
  };

  const stop = () => {
    runningRef.current = false;
    clearTimeout(timerRef.current);
    clearVisualTimeouts();
  };

  const playSample = (buffer, time) => {
    const ctx = audioCtxRef.current;
    const src = new AudioBufferSourceNode(ctx, { buffer });
    src.connect(ctx.destination);
    src.start(time);
    src.onended = () => src.disconnect();
  };

  const scheduleNote = (beat, time) => {
    const { sequence, onCurrentBeatChange } = propsRef.current;
    const step = Number(sequence[beat]); // true → 1, false → 0
    if (step > 0) {
      const buffer = buffersRef.current[step - 1]; // 1-based → 0-based
      if (buffer) playSample(buffer, time);
    }
    if (onCurrentBeatChange) {
      const delay = Math.max(0, (time - audioCtxRef.current.currentTime) * 1000);
      visualTimeoutsRef.current.push(setTimeout(() => onCurrentBeatChange(beat), delay));
    }
  };

  // Returns false when playback should end.
  const advance = () => {
    const { sequence, bpm, loop, reset_playback, setIsPlaying, isPlaylist, nextelement } =
      propsRef.current;

    nextNoteTimeRef.current += 60.0 / bpm / 4; // 16th notes
    currentBeatRef.current++;

    if (currentBeatRef.current < sequence.length) return true;

    if (!loop) {
      // Let the last visual beat land, then report the end.
      const delay = Math.max(0, (nextNoteTimeRef.current - audioCtxRef.current.currentTime) * 1000);
      setTimeout(() => {
        if (reset_playback) reset_playback();
        if (setIsPlaying) setIsPlaying(false);
      }, delay);
      return false;
    }

    if (isPlaylist && nextelement) {
      const delay = (nextNoteTimeRef.current - audioCtxRef.current.currentTime) * 1000;
      const ok = nextelement();
      if (ok === true && setIsPlaying) {
        setIsPlaying(false);
        setTimeout(() => setIsPlaying(true), Math.max(0, delay));
      }
      return false;
    }

    currentBeatRef.current = 0;
    return true;
  };

  const scheduler = () => {
    if (!runningRef.current) return;
    const ctx = audioCtxRef.current;
    while (nextNoteTimeRef.current < ctx.currentTime + scheduleAheadTime) {
      scheduleNote(currentBeatRef.current, nextNoteTimeRef.current);
      if (!advance()) {
        runningRef.current = false;
        return;
      }
    }
    timerRef.current = setTimeout(scheduler, lookahead);
  };

  // Start / stop
  useEffect(() => {
    if (!isPlaying) { stop(); return; }

    let cancelled = false;
    const ctx = getCtx();
    stop();

    // Wait for resume() so currentTime is actually moving before scheduling.
    (ctx.state === "suspended" ? ctx.resume() : Promise.resolve()).then(() => {
      if (cancelled) return;
      if (!buffersRef.current.length) {
        console.warn("RhythmPlayback: started before samples finished loading");
      }
      currentBeatRef.current = 0;
      nextNoteTimeRef.current = ctx.currentTime + 0.05;
      runningRef.current = true;
      scheduler();
    });

    return () => { cancelled = true; stop(); };
  }, [isPlaying]);

  // Unmount
  useEffect(() => () => {
    stop();
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }, []);

  return null;
};

export default RhythmPlayback;
